/**
 * 3D Planet — "Controleer je ontwerp" (blocks/tdp-design-check.liquid), naar Vistaprint's controlestap:
 * actuele tekst, keuzes, totaal en een verplicht akkoord-vinkje
 * vóór "In winkelwagen". designWarnings() is een zuivere functie (tests/tdp-design-check.test.mjs).
 */

const ACCELERATED_CHECKOUT_SELECTOR = '.accelerated-checkout-block';
const LONG_CAPS_MIN = 8;
const PRINTABLE = /^[\p{Script=Latin}\p{N}\p{P}\p{Zs}€°²³½&+=@#'’"-]*$/u;

/**
 * Waarschuwingen over een personalisatietekst. `blocking` = zo printen we het niet.
 * @param {string} text
 * @param {{ maxLength?: number, required?: boolean }} [options]
 * @returns {{ code: string, level: 'error'|'warning', blocking: boolean }[]}
 */
export function designWarnings(text, { maxLength = 20, required = true } = {}) {
  const raw = String(text ?? '');
  const trimmed = raw.trim();
  const warnings = [];
  if (trimmed.length === 0) {
    if (required) warnings.push({ code: 'empty', level: 'error', blocking: true });
    return warnings;
  }
  if (!PRINTABLE.test(trimmed)) warnings.push({ code: 'unsupported', level: 'error', blocking: true });
  if (raw !== trimmed) warnings.push({ code: 'outer_spaces', level: 'warning', blocking: false });
  if (/\s{2,}/.test(trimmed)) warnings.push({ code: 'double_spaces', level: 'warning', blocking: false });
  if (maxLength > 0 && [...trimmed].length >= maxLength) warnings.push({ code: 'at_limit', level: 'warning', blocking: false });
  const letters = trimmed.replace(/[^\p{L}]/gu, '');
  if (letters.length >= LONG_CAPS_MIN && letters === letters.toUpperCase()) warnings.push({ code: 'long_caps', level: 'warning', blocking: false });
  if (/(.)\1{3,}/.test(trimmed)) warnings.push({ code: 'repeat', level: 'warning', blocking: false });
  return warnings;
}

const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class TdpDesignCheck extends BaseElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.approve = this.querySelector('[data-tdp-check-approve]');
    this.summary = this.querySelector('[data-tdp-check-summary]');
    this.hint = this.querySelector('[data-tdp-check-hint]');
    this.totalSlot = this.querySelector('[data-tdp-check-total]');
    this.error = this.querySelector('[data-tdp-check-error]');
    this.root = this.closest('.shopify-section') || this.parentElement;
    this.optionNames = JSON.parse(this.dataset.optionNames || '[]');
    this.options = JSON.parse(this.dataset.options || '[]');
    this.approve.checked = false;
    this.approve.setAttribute('aria-describedby', this.error.id);
    this.root.addEventListener('input', () => this.render(), { signal });
    this.root.addEventListener('change', () => this.render(), { signal });
    this.root.addEventListener('quantity-selector:update', () => this.render(), { signal });
    this.root.addEventListener('tdp:total-update', () => this.render(), { signal });
    this.root.addEventListener('shopify:product:select', (event) => {
      if (event.target.closest('product-card')) return;
      this.pending = true;
      this.approve.checked = false;
      this.render();
      const request = this.request = event.promise;
      request.then(({ detail }) => {
        if (!this.isConnected || this.request !== request) return;
        this.pending = false;
        this.options = detail?.resource?.options || this.options;
        if (detail?.resource?.price != null) this.dataset.unitCents = detail.resource.price;
        this.render();
      }).catch(() => { /* Keep approval blocked until a variant resolves successfully. */ });
    }, { signal });
    this.root.addEventListener('submit', (event) => {
      if (event.target === this.form) this.validateAttempt(event);
    }, { signal, capture: true });
    this.root.addEventListener('click', (event) => {
      const button = event.target.closest('button, input[type="submit"]');
      if (button?.form === this.form && button.type === 'submit') this.validateAttempt(event);
    }, { signal, capture: true });
    this.root.addEventListener('invalid', (event) => {
      if (event.target.form === this.form) this.validateAttempt(event);
    }, { signal, capture: true });
    this.render();
  }

  disconnectedCallback() { this.abort?.abort(); }
  get form() { return document.getElementById(this.dataset.formId); }
  get fields() {
    return [...this.root.querySelectorAll('[data-tdp-text-field]')].filter(field => field.form === this.form);
  }

  fieldIssue(field) {
    if (field.required && !field.value.trim()) return 'Vul een naam of tekst in.';
    if (designWarnings(field.value, { required: field.required, maxLength: field.maxLength }).some(w => w.blocking)) return 'Gebruik letters, cijfers en leestekens; geen emoji.';
    if (field.maxLength > 0 && [...field.value].length > field.maxLength) return `Gebruik maximaal ${field.maxLength} tekens.`;
    if (!field.validity.valid) return 'Controleer dit veld.';
    return '';
  }

  showFieldError(field, message) {
    let error = document.getElementById(`${field.id}-error`);
    if (!error) {
      error = document.createElement('p');
      error.id = `${field.id}-error`;
      error.className = 'tdp-field-error';
      error.setAttribute('role', 'alert');
      field.parentElement.after(error);
      field.setAttribute('aria-describedby', `${field.getAttribute('aria-describedby') || ''} ${error.id}`.trim());
    }
    error.textContent = message;
    error.hidden = !message;
    field.setAttribute('aria-invalid', String(Boolean(message)));
  }

  validateAttempt(event) {
    this.render();
    const field = this.fields.find(field => this.fieldIssue(field));
    const other = [...(this.form?.elements || [])].find(el => el !== this.approve && el.willValidate && !el.validity.valid);
    if (!field && !other && !this.pending && this.approve.checked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (field || other) {
      const target = field || other;
      this.showFieldError(target, field ? this.fieldIssue(field) : 'Controleer dit veld.');
      target.focus();
    } else {
      this.error.textContent = this.pending ? 'Even wachten tot je keuze is geladen.' : 'Controleer je keuzes en vink het akkoord aan.';
      this.error.hidden = false;
      this.approve.focus();
    }
  }

  render() {
    if (!this.approve) return;
    const fields = this.fields;
    const quantity = this.form?.elements.namedItem('quantity')?.value || '1';
    const signature = JSON.stringify([fields.map(f => f.value), this.options, quantity, this.dataset.unitCents,
      [...this.root.querySelectorAll('variant-picker input:checked, variant-picker select')].map(el => el.value)]);
    if (signature !== this.signature) {
      this.approve.checked = false;
      this.signature = signature;
      this.error.hidden = true;
    }
    for (const field of fields) {
      if (document.getElementById(`${field.id}-error`)) this.showFieldError(field, this.fieldIssue(field));
    }
    const hasText = fields.some(field => field.value.trim());
    const blocking = this.pending || fields.some(field => this.fieldIssue(field)) ||
      [...(this.form?.elements || [])].some(el => el !== this.approve && el.willValidate && !el.validity.valid);
    this.approve.disabled = Boolean(blocking);
    this.hint.hidden = hasText;
    this.querySelector('.tdp-check__title').hidden = !hasText;
    this.approve.closest('label').hidden = !hasText;
    this.summary.hidden = !hasText;
    this.totalSlot.hidden = !hasText;
    const entries = fields.filter(f => f.value.trim()).map(f => [f.dataset.label || 'Naam / tekst', f.value]);
    this.optionNames.forEach((name, index) => {
      if (name !== 'Title') entries.push([name === 'Kleur' ? 'Productkleur' : name, this.options[index] || '—']);
    });
    this.summary.replaceChildren(...entries.flatMap(([label, value]) => {
      const dt = document.createElement('dt'); dt.textContent = label;
      const dd = document.createElement('dd'); dd.textContent = value;
      return [dt, dd];
    }));
    // Reuse the existing tier total in this control point, without a second amount.
    const tiers = [...this.root.querySelectorAll('tdp-tier-buttons')].find(el => el.dataset.formId === this.dataset.formId);
    const total = tiers?.querySelector('[data-tier-total]');
    if (total) this.totalSlot.replaceChildren(total);
    if (!tiers) {
      const fmt = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
      const cents = Number(this.dataset.unitCents);
      const unitWord = (this.dataset.unit || this.closest('[data-unit]')?.dataset.unit) === 'set' ? (Number(quantity) === 1 ? 'set' : 'sets') : (Number(quantity) === 1 ? 'stuk' : 'stuks');
      this.totalSlot.textContent = `${quantity} ${unitWord} × ${fmt.format(cents / 100)} = ${fmt.format(cents * Number(quantity) / 100)} incl. btw`;
    }
    this.dataset.state = blocking ? 'blocked' : this.approve.checked ? 'approved' : 'pending';
    const checkout = this.form?.querySelector(ACCELERATED_CHECKOUT_SELECTOR);
    if (checkout) {
      const hide = Boolean(blocking || !this.approve.checked);
      checkout.hidden = hide;
      checkout.toggleAttribute('inert', hide);
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tdp-design-check')) customElements.define('tdp-design-check', TdpDesignCheck);
